import { isFunction } from 'lodash-es';
import type { ChangeEvent, FC, ReactElement, ReactNode } from 'react';

import {
    Box,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    styled,
    type SelectProps,
} from '@mui/material';

import { Field } from 'common/component/ValidationFormAndField';
import { BodyText } from 'common/typography/typography';
import { SearchBarOutlined } from 'common/viewcomp/inputs/SearchBarOutlined';

import '../form_fields.scss';

import type { CIFieldProps } from './fields.interface';

export interface CIDropdownProps extends Omit<CIFieldProps, 'labelI18NKey'> {
    labelI18NKey: string | null;
    showErrorIfModified?: boolean;
    tooltip?: string;
    showClear?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    onSearchChange?: (searchTerm: string) => void;
    searchValue?: string;
    formOnChangeOverride?: (event: ChangeEvent<HTMLSelectElement>, formOnChange: (value: unknown) => void) => void;
}

export const CIDropDown: FC<CIDropdownProps & Pick<SelectProps, 'startAdornment'>> = (props): ReactElement => {
    const {
        name,
        id,
        t,
        labelI18NKey,
        className = '',
        required,
        children,
        variant = 'standard',
        validations = [],
        fullWidth,
        disabled,
        margin,
        onChange,
        iconComponent,
        onOpen,
        onClose,
        menuProps,
        multiple,
        multiline = false,
        labelId,
        disableUnderline = false,
        displayEmpty = false,
        renderValue,
        showErrorOnLoad = false,
        showErrorIfModified = false,
        ariaLabel = null,
        readOnly = false,
        tooltip = null,
        helperText = null,
        formOnChangeOverride,
        showClear = false, // default can be !required
        startAdornment = null,
        style,
        value,
        searchable = false,
        searchPlaceholder = 'Search...',
        onSearchChange,
        searchValue = '',
        dataTestId,
    } = props;

    // If `variant` is outlined then `disableUnderline` prop is not valid for Select Input
    const disableUnderlineProp = variant !== 'outlined' ? { disableUnderline } : {};

    const handlePropagation = (event: any) => {
        event.stopPropagation();
    };

    return (
        <Field
            t={t}
            id={id}
            name={name}
            validations={validations}
            data-testid="ci-dropdown-field"
            render={({ input, meta }): ReactNode => {
                const handleChange = (e: ChangeEvent<HTMLSelectElement>): void => {
                    const formOnChange = input?.onChange;
                    if (!isFunction(formOnChangeOverride)) {
                        formOnChange(e.target.value);
                    } else {
                        formOnChangeOverride(e, formOnChange);
                    }
                    onChange && onChange(e); //Parent OnChange
                };

                const handleOpen = (event: any) => {
                    onSearchChange && onSearchChange('');
                    onOpen && onOpen(event);
                };

                let showError = showErrorOnLoad ? !!meta.error : meta.error && meta.touched;
                if (!showError && showErrorIfModified) {
                    showError = meta.error && meta.modified;
                }

                let select = (
                    <Select
                        data-testid="ci-dropdown-select"
                        multiple={multiple}
                        inputProps={{
                            name,
                            id,
                            'aria-label': ariaLabel,
                            readOnly,
                        }}
                        fullWidth={fullWidth}
                        {...input}
                        onChange={handleChange}
                        IconComponent={iconComponent}
                        onOpen={handleOpen}
                        onClose={onClose}
                        MenuProps={menuProps}
                        labelId={labelId}
                        multiline={multiline}
                        displayEmpty={displayEmpty}
                        renderValue={renderValue}
                        {...disableUnderlineProp}
                        startAdornment={startAdornment}
                        style={style}
                        value={value !== undefined ? value : input.value}
                    >
                        {searchable && (
                            <Box sx={{ px: 2, py: 1 }}>
                                <SearchBarOutlined
                                    label={searchPlaceholder}
                                    searchBar={{
                                        value: searchValue,
                                        inputValue: searchValue,
                                        onChange: onSearchChange,
                                        onClear: () => onSearchChange(''),
                                    }}
                                    onClick={handlePropagation}
                                    onKeyDown={handlePropagation}
                                />
                            </Box>
                        )}

                        {showClear && (
                            <MenuItem value={undefined}>
                                <ClearText>{t('table.clear')}</ClearText>
                            </MenuItem>
                        )}

                        {children}
                    </Select>
                );
                if (tooltip) {
                    select = <Tooltip title={tooltip}>{select}</Tooltip>;
                }

                return (
                    // CIDrop-Down class used to override some RTL issues
                    <div
                        data-testid={dataTestId}
                        className={`CIDrop-Down ${input?.value?.length ? 'has-value' : ''}  ${className} `}
                    >
                        <FormControl
                            data-testid="ci-dropdown-form-control"
                            error={showError}
                            variant={variant}
                            fullWidth={fullWidth}
                            margin={margin}
                            disabled={disabled}
                            style={style}
                        >
                            <InputLabel htmlFor={id} required={required} variant={variant}>
                                {labelI18NKey && t(labelI18NKey)}
                            </InputLabel>
                            {select}
                            {showError ? <FormHelperText>{t(meta.error)}</FormHelperText> : <></>}
                            {helperText && <FormHelperText>{helperText}</FormHelperText>}
                        </FormControl>
                    </div>
                );
            }}
        />
    );
};

const ClearText = styled(BodyText)(({ theme }) => ({
    color: theme.ciPalette.common.blue,
}));
